import { useCallback } from 'react';

/**
 * A hook to standardize keyboard navigation between list items.
 * @param focusIndex currently focused item
 * @param list of items to navigate
 * @param setFocusIndex setter for the chosen index
 * @param onChosen handler for selecting an item
 */
export function useListKeyboardNav<T>(
  focusIndex: number,
  list: T[],
  setFocusIndex: React.Dispatch<React.SetStateAction<number>>,
  onChosen: (value: T) => void
) {
  const keyboardNavHandler = useCallback(
    (e: React.KeyboardEvent) => {
      e.preventDefault();
      switch (e.key) {
        case 'ArrowDown':
          setFocusIndex(focusIndex + 1 === list.length ? 0 : Math.min(focusIndex + 1, list.length - 1));
          break;
        case 'ArrowUp':
          setFocusIndex(Math.max(focusIndex - 1, 0));
          break;
        case 'Enter':
          focusIndex >= 0 && focusIndex < list.length && onChosen(list[focusIndex]);
          break;
        case 'Escape':
          setFocusIndex(-1);
          break;
        default:
          break;
      }
    },
    [focusIndex, list, onChosen, setFocusIndex]
  );

  return { keyboardNavHandler };
}
